/**
 * The optimistic list/total math behind `useUnifiedInbox` — a click drops a row
 * and its count NOW, the mutation + refetch true it up. Pure (rows + totals in,
 * rows + totals out) so the arithmetic is unit-tested apart from the singleton's
 * refs. Each returns the NEW list plus what changed; the composable assigns them.
 */

/** Adjust a section total + the `all` total by a signed delta, floored at 0. */
export function adjustTotals(totals, section, delta) {
  return {
    ...totals,
    [section]: Math.max(0, (totals[section] ?? 0) + delta),
    all: Math.max(0, (totals.all ?? 0) + delta),
  }
}

/**
 * Remove the first matching row. Returns `{ rows, dropped, matched }` —
 * `dropped` is the count the totals should shed (`total` ?? `count` ?? 1),
 * `matched` false when nothing matched (the caller then leaves state untouched).
 */
export function dismissRow(rows, match) {
  const row = (rows ?? []).find(match)
  if (!row) return { rows: rows ?? [], dropped: 0, matched: false }

  return {
    rows: rows.filter(r => r !== row),
    dropped: row.total ?? row.count ?? 1,
    matched: true,
  }
}

/**
 * Clear ONE signal (notes | messages) on an entity's row. The row survives while
 * the OTHER signal still has a count (its `total`/`latestAt` re-point to it);
 * once both are quiet it leaves. Returns `{ rows, dropped }` — the cleared
 * signal's count, 0 when the row/signal was already empty.
 */
export function clearSignal(rows, entityId, signal) {
  const row = (rows ?? []).find(r => String(r.entityId) === String(entityId))
  if (!row) return { rows: rows ?? [], dropped: 0 }

  const dropped = row[signal]?.count ?? 0
  if (!dropped) return { rows: rows ?? [], dropped: 0 }

  const other = signal === 'notes' ? 'messages' : 'notes'

  const cleared = {
    ...row,
    [signal]: { count: 0, latestAt: null, snippet: null },
    total: row[other]?.count ?? 0,
    latestAt: row[other]?.latestAt ?? null,
    ...(signal === 'notes' ? { noteAuthors: [] } : {}),
  }

  return {
    rows:
      cleared.total > 0
        ? rows.map(r => (r === row ? cleared : r))
        : rows.filter(r => r !== row),
    dropped,
  }
}

/**
 * Flip `handled` in place on the matching calls (rows are never removed — in the
 * All lens a returned call stays visible, greyed). Returns `{ rows, delta }` —
 * the signed change to the missed-calls/`all` totals (negative when handling).
 */
export function flipCallsHandled(rows, recordIds, handled) {
  const ids = new Set((recordIds ?? []).map(String))
  let delta = 0

  const next = (rows ?? []).map(row => {
    if (!ids.has(String(row.recordId)) || row.handled === handled) return row

    delta += handled ? -(row.count ?? 1) : (row.count ?? 1)

    return { ...row, handled }
  })

  return { rows: next, delta }
}
